import { Button } from "newui/components/Button";
import { Modal } from "newui/components/ui/Modal";
import { QuoteResourceType } from "newui/features/Rfx/api/procurer-quotes-api";
import { CreateQuoteTableModal, DynamicQuoteTable, ExistingValueColumnSource, QuoteColumnType, QuoteTableColumn, QuoteTableConfig } from "newui/features/Rfx/components/QuoteTable";
import { RfxQuoteTable } from "newui/features/Rfx/Submission/types/quote.type";
import { useEffect, useMemo, useRef, useState } from "react";
import { FiAlertCircle, FiEdit2, FiMenu, FiPlus, FiSave } from "react-icons/fi";
import { cn } from "utils/helpers";

const MAX_COLUMNS = 20;

interface QuoteTableCardProps {
    columnValues?: Record<number, Record<number, string>>;
    descError?: boolean;
    dirtyRowValues?: Record<number, { unit_id?: number | null; name?: string; }>;
    disabled: boolean;
    dragHandleProps: Record<string, unknown> | null | undefined;
    hasError?: boolean;
    isDeletable: boolean;
    nameError?: boolean;
    quoteTable: RfxQuoteTable;
    resourceType: QuoteResourceType;
    rowErrors?: Record<number, { unit_id?: boolean; name?: boolean; }>;
    siblingValueColumns: ExistingValueColumnSource[];
    unitOptions: { label: string; value: number }[];
    onAddRow: () => void;
    onColumnAdd: (columnType: QuoteColumnType, label: string) => void;
    onColumnDelete: (columnId: number) => void;
    onColumnLabelChange: (columnId: number, label: string) => void;
    onColumnReorder: (columnIds: number[]) => void;
    onCopy: () => void;
    onDelete: () => void;
    onDeleteRow: (rowId: number) => void;
    onEditLayout: (config: QuoteTableConfig) => void;
    onReorderRows: (rowIds: number[]) => void;
    onSaveAsTemplate: (name: string) => void;
    onToggleTotalsRow: () => void;
    onUpdateDescription: (description: string) => void;
    onUpdateName: (name: string) => void;
    onUpdateRow: (rowId: number, payload: { unit_id?: number | null; name?: string; }) => void;
    onCellValueChange?: (rowId: number, columnId: number, field: string, value: string) => void;
}

export const QuoteTableCard: React.FC<QuoteTableCardProps> = ({
    disabled,
    dragHandleProps,
    isDeletable,
    onAddRow,
    onColumnAdd,
    onColumnDelete,
    onColumnLabelChange,
    onColumnReorder,
    onCopy,
    onDelete,
    onDeleteRow,
    onEditLayout,
    onReorderRows,
    onSaveAsTemplate,
    onUpdateDescription,
    onUpdateName,
    onUpdateRow,
    onToggleTotalsRow,
    columnValues = {},
    onCellValueChange,
    dirtyRowValues = {},
    hasError = false,
    nameError = false,
    descError = false,
    rowErrors = {},
    quoteTable,
    siblingValueColumns,
    unitOptions,
    resourceType,
}) => {
    const [nameValue, setNameValue] = useState(quoteTable.name || "");
    const [descValue, setDescValue] = useState(quoteTable.description || "");
    const [pendingDeleteTable, setPendingDeleteTable] = useState(false);
    const [showSaveTemplate, setShowSaveTemplate] = useState(false);
    const [templateName, setTemplateName] = useState("");
    const [showLayoutModal, setShowLayoutModal] = useState(false);
    const [pendingLayoutConfig, setPendingLayoutConfig] = useState<QuoteTableConfig | null>(null);
    const nameHasFocus = useRef(false);
    const descHasFocus = useRef(false);

    useEffect(() => {
        if (!nameHasFocus.current) {
            setNameValue(quoteTable.name || "");
        }
        if (!descHasFocus.current) {
            setDescValue(quoteTable.description || "");
        }
    }, [quoteTable.name, quoteTable.description]);

    const handleNameBlur = () => {
        nameHasFocus.current = false;
        if (nameValue !== quoteTable.name) {
            onUpdateName(nameValue);
        }
    };

    const handleDescBlur = () => {
        descHasFocus.current = false;
        if (descValue !== quoteTable.description) {
            onUpdateDescription(descValue);
        }
    };

    const columns: QuoteTableColumn[] = useMemo(() => {
        if (quoteTable.columns && quoteTable.columns.length > 0) {
            return quoteTable.columns.map((col) => ({
                id: col.id,
                columnType: col.columnType,
                label: col.name,
                sortOrder: col.sortOrder,
                isDefault: false,
                isSupplierEditable: col.isSupplierEditable,
            }));
        }
        return [
            { id: -1, columnType: "description" as const, label: "#", sortOrder: 0, isDefault: true },
            { id: -2, columnType: "description" as const, label: "Description of Scope", sortOrder: 1, isDefault: true },
            { id: -3, columnType: "unit_of_measurement" as const, label: "Unit of Measurement", sortOrder: 2, isDefault: true },
            { id: -4, columnType: "response_quantity" as const, label: "Quantity", sortOrder: 3, isDefault: true },
            { id: -5, columnType: "response_value" as const, label: "Rate", sortOrder: 4, isDefault: true },
            { id: -6, columnType: "total" as const, label: "Cost", sortOrder: 5, isDefault: true },
        ];
    }, [quoteTable.columns]);

    // Show categories from sibling tables (not this table) for cross-table picker
    const existingValueColumns: ExistingValueColumnSource[] = useMemo(() => {
        return siblingValueColumns.filter((src) => src.quoteId !== quoteTable.id);
    }, [siblingValueColumns, quoteTable.id]);

    return (
        <div className={cn(
            "my-6 w-full rounded-md border bg-white px-8 pb-2 pt-3",
            hasError ? "border-red-500" : "border-gray-200"
        )}>
            {hasError && (
                <div className="mb-3 flex items-center gap-2 rounded bg-red-50 px-3 py-2">
                    <FiAlertCircle className="text-red-500" size={14} />
                    <span className="text-sm text-red-700">
                        Please resolve all errors before {resourceType === "rfx" ? "sending" : "saving"} your RFx{resourceType === "rfx-template" && " template"}.
                    </span>
                </div>
            )}
            <div className="mb-4 flex items-start justify-between">
                <div className="flex flex-1 items-start gap-2">
                    {dragHandleProps && (
                        <div
                            {...dragHandleProps}
                            className="mt-1 cursor-grab text-gray-400"
                        >
                            <FiMenu size={20} />
                        </div>
                    )}
                    <div className="flex-1 flex-col gap-2">
                        <input
                            className={cn(
                                "w-[300px] rounded border px-2 py-1 font-roboto text-lg font-semibold text-primary-900",
                                disabled
                                    ? "border-transparent bg-transparent"
                                    : (nameError || !nameValue.trim())
                                        ? "border-red-500 bg-white"
                                        : "border-gray-300 bg-white"
                            )}
                            disabled={disabled}
                            onBlur={handleNameBlur}
                            onChange={(e) => setNameValue(e.target.value)}
                            onFocus={() => { nameHasFocus.current = true; }}
                            placeholder="Table name"
                            value={nameValue}
                        />
                        <input
                            className={cn(
                                "mt-1 w-[90%] rounded border px-2 py-1 text-sm text-tertiary-600",
                                disabled
                                    ? "border-transparent bg-transparent"
                                    : (descError || !descValue.trim())
                                        ? "border-red-500 bg-white"
                                        : "border-gray-300 bg-white"
                            )}
                            disabled={disabled}
                            onBlur={handleDescBlur}
                            onChange={(e) => setDescValue(e.target.value)}
                            onFocus={() => { descHasFocus.current = true; }}
                            placeholder="Description"
                            value={descValue}
                        />
                    </div>
                </div>
                {!disabled && (
                    <div className="flex items-center gap-2">
                        <button
                            className="flex items-center gap-1 rounded p-1 text-sm text-gray-500 hover:bg-gray-100"
                            data-testid="edit-layout-btn"
                            onClick={() => setShowLayoutModal(true)}
                            title="Edit table layout"
                            type="button"
                        >
                            <FiEdit2 size={14} />
                            Edit Table Layout
                        </button>
                        <button
                            className="flex items-center gap-1 rounded p-1 text-sm text-gray-500 hover:bg-gray-100"
                            onClick={() => {
                                setShowSaveTemplate(true);
                                setTemplateName(quoteTable.name || "");
                            }}
                            title="Save as template"
                            type="button"
                        >
                            <FiSave size={14} />
                            Save as Template
                        </button>
                        <button
                            className="rounded p-1 text-sm text-gray-500 hover:bg-gray-100"
                            onClick={onCopy}
                            title="Copy table"
                            type="button"
                        >
                            Copy
                        </button>
                        {isDeletable && (
                            <button
                                className="rounded p-1 text-sm text-red-500 hover:bg-red-50"
                                onClick={() => setPendingDeleteTable(true)}
                                title="Delete table"
                                type="button"
                            >
                                Delete
                            </button>
                        )}
                    </div>
                )}
            </div>

            <DynamicQuoteTable
                columns={columns}
                columnValues={columnValues}
                disabled={disabled}
                dirtyRowValues={dirtyRowValues}
                includeTotalsRow={quoteTable.includeTotalsRow === true}
                onCellValueChange={onCellValueChange}
                rowErrors={rowErrors}
                items={quoteTable.rows || []}
                mode="edit"
                onRowDelete={onDeleteRow}
                onRowReorder={onReorderRows}
                onRowUpdate={onUpdateRow}
                unitOptions={unitOptions}
            />

            {!disabled && (
                <div className="my-4 flex items-center justify-between">
                    <Button
                        btntype="plain-border"
                        onClick={onAddRow}
                        type="button"
                    >
                        <FiPlus className="mr-1" />
                        Add Row
                    </Button>
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                        <span>Show Totals Row</span>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={quoteTable.includeTotalsRow === true}
                            className={cn(
                                "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors",
                                quoteTable.includeTotalsRow ? "bg-blue-600" : "bg-gray-300"
                            )}
                            onClick={onToggleTotalsRow}
                        >
                            <span
                                className={cn(
                                    "pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transition-transform",
                                    quoteTable.includeTotalsRow ? "translate-x-4" : "translate-x-0"
                                )}
                            />
                        </button>
                    </label>
                </div>
            )}

            <Modal
                iconType="error"
                isOpen={pendingDeleteTable}
                modalType="standard"
                onClose={() => setPendingDeleteTable(false)}
                primaryButton={{
                    btntype: "primary",
                    onClick: () => {
                        setPendingDeleteTable(false);
                        onDelete();
                    },
                    text: "Delete",
                }}
                secondaryButton={{
                    onClick: () => setPendingDeleteTable(false),
                    text: "Cancel",
                }}
                title="Delete Quote Table"
            >
                <div className="text-center text-md text-gray-600 gap-2 flex flex-col">
                    <p>
                        Are you sure you want to delete &quot;{quoteTable.name || "this table"}&quot;?
                    </p>
                    <p>
                        This will remove all rows and columns.
                    </p>
                    <p>
                        This action cannot be undone.
                    </p>
                </div>

            </Modal>

            {showSaveTemplate && (
                <div className="my-4 rounded border border-blue-200 bg-blue-50 p-4">
                    <p className="mb-2 text-sm font-medium text-blue-900">
                        Save as Template
                    </p>
                    <p className="mb-3 text-xs text-blue-700">
                        Save this table&apos;s structure (columns and rows) as a reusable template.
                    </p>
                    <input
                        autoFocus
                        className="mb-3 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                        onChange={(e) => setTemplateName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && templateName.trim()) {
                                setShowSaveTemplate(false);
                                onSaveAsTemplate(templateName.trim());
                            }
                        }}
                        placeholder="Template name"
                        value={templateName}
                    />
                    <div className="flex gap-2">
                        <button
                            className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                            disabled={!templateName.trim()}
                            onClick={() => {
                                setShowSaveTemplate(false);
                                onSaveAsTemplate(templateName.trim());
                            }}
                            type="button"
                        >
                            Save Template
                        </button>
                        <button
                            className="rounded border border-gray-300 px-3 py-1 text-sm text-gray-600 hover:bg-gray-100"
                            onClick={() => setShowSaveTemplate(false)}
                            type="button"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
            <CreateQuoteTableModal
                unitOptions={unitOptions}
                existingColumns={
                    quoteTable.columns && quoteTable.columns.length > 0
                        ? quoteTable.columns.map((col, idx) => ({
                            id: col.id,
                            columnType: col.columnType,
                            label: col.name,
                            sortOrder: col.sortOrder ?? idx,
                            localId: `existing-${col.id}`,
                        }))
                        : undefined
                }
                existingRows={
                    quoteTable.rows && quoteTable.rows.length > 0
                        ? quoteTable.rows.map((row) => {
                            // Re-key cells from column-id → localId so the modal
                            // (which uses synthesised `existing-${col.id}` localIds)
                            // can read each cell against the right column.
                            const cells: Record<string, string> = {};
                            row.cells?.forEach((cell) => {
                                if (cell.value != null) {
                                    cells[`existing-${cell.rfxQuoteColumnId}`] = cell.value;
                                }
                            });
                            return {
                                unit_id: row.unitId,
                                sort_order: row.sortOrder,
                                cells,
                            };
                        })
                        : undefined
                }
                existingDescription={quoteTable.description || ""}
                existingTableName={quoteTable.name || ""}
                isOpen={showLayoutModal}
                onClose={() => setShowLayoutModal(false)}
                onSubmit={(config) => {
                    setShowLayoutModal(false);
                    const existingIds = new Set((quoteTable.columns || []).map((c) => c.id));
                    const newIds = new Set(config.columns.filter((c) => c.id).map((c) => c.id as number));
                    const hasRemovedColumns = [...existingIds].some((id) => !newIds.has(id));
                    if (hasRemovedColumns && (quoteTable.rows?.length || 0) > 0) {
                        setPendingLayoutConfig(config);
                    } else {
                        onEditLayout(config);
                    }
                }}
            />

            <Modal
                iconType="error"
                isOpen={pendingLayoutConfig !== null}
                modalType="alert"
                onClose={() => setPendingLayoutConfig(null)}
                primaryButton={{
                    btntype: "primary",
                    onClick: () => {
                        if (pendingLayoutConfig) {
                            onEditLayout(pendingLayoutConfig);
                        }
                        setPendingLayoutConfig(null);
                    },
                    text: "Remove Columns",
                }}
                secondaryButton={{
                    onClick: () => setPendingLayoutConfig(null),
                    text: "Cancel",
                }}
                title="Remove Columns?"
            >
                <p className="text-center text-sm text-gray-600">
                    Removing columns will permanently delete any data entered in those
                    columns across all rows. This action cannot be undone.
                </p>
            </Modal>
        </div>
    );
};